// src/client/screens/borrower/LoanApplyScreen.tsx
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star } from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { LOAN_TERM_DAYS, type LoanTermDays } from '@/constants/config';
import { useCreditScore } from '@client/hooks/useCreditScore';
import { kwachaToNgwee, type Ngwee } from '@/lib/types';
import { formatKwacha } from '@client/utils/currency';
import { maxLoanForTier } from '@client/utils/creditScoreUtils';
import { saveLoanDraft } from '@client/lib/loanDraft';
import AppHeader from '@client/components/AppHeader';
import Button from '@client/components/Button';
import NavBar from '@client/components/NavBar';
import { useToast } from '@client/components/ToastProvider';
import type { CreditTier } from '@client/lib/creditScore.types';

const MIN_LOAN_KWACHA = 50; // K50
const MIN_LOAN = kwachaToNgwee(MIN_LOAN_KWACHA);

const TERM_LABELS: Record<LoanTermDays, string> = {
  14: '2 weeks',
  30: '4 weeks',
  60: '8 weeks',
  90: '12 weeks',
};

const TIER_DISPLAY: Record<CreditTier, string> = {
  new: 'New Member',
  building: 'Building Tier',
  established: 'Established Tier',
  strong: 'Strong Tier',
  excellent: 'Excellent Tier',
};

// Full-circle SVG gauge (score 0–100)
function ScoreRing({ score }: { score: number }) {
  const R = 22;
  const CIRC = 2 * Math.PI * R;
  const pct = Math.max(0, Math.min(100, score)) / 100;
  const dashArr = `${pct * CIRC} ${CIRC}`;
  return (
    <div style={{ position: 'relative', width: 56, height: 56, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      <svg width={56} height={56} style={{ position: 'absolute', top: 0, left: 0 }}>
        <circle cx={28} cy={28} r={R} stroke="rgba(255,255,255,0.25)" strokeWidth={4} fill="none" />
        <circle
          cx={28} cy={28} r={R}
          stroke="#ffffff" strokeWidth={4} fill="none"
          strokeDasharray={dashArr}
          strokeDashoffset={CIRC * 0.25}   /* start at 12 o'clock */
          strokeLinecap="round"
          transform="rotate(-90 28 28)"
        />
      </svg>
      <span style={{ fontSize: 12, fontWeight: 700, color: '#ffffff', position: 'relative', zIndex: 1 }}>
        {score}
      </span>
    </div>
  );
}

export default function LoanApplyScreen() {
  const { data: scoreData } = useCreditScore();
  const navigate = useNavigate();

  const tier = scoreData?.tier ?? 'new';
  const maxKwacha = Math.round(maxLoanForTier(tier) / 100);
  const score = scoreData?.score ?? 0;

  const [amountStr, setAmountStr] = useState('');
  const [term, setTerm] = useState<LoanTermDays>(LOAN_TERM_DAYS[1]); // 30 days default
  const [purpose, setPurpose] = useState('');
  const toast = useToast();

  const handleApply = () => {
    const kwacha = parseFloat(amountStr);
    if (!kwacha || kwacha < MIN_LOAN_KWACHA) {
      toast.show(`Minimum loan amount is K${MIN_LOAN_KWACHA.toLocaleString()}`, 'error');
      return;
    }
    if (kwacha > maxKwacha) {
      toast.show(`Maximum for your tier is K${maxKwacha.toLocaleString()}`, 'error');
      return;
    }
    const principal = kwachaToNgwee(kwacha) as Ngwee;
    saveLoanDraft({ principal, term, purpose: purpose.trim() || undefined });
    navigate(ROUTES.loanConfirm);
  };

  return (
    <div className="app-shell app-shell--with-nav">
      <AppHeader />

      <main className="scroll-area" style={{ padding: '0 16px 24px' }}>
        {/* Screen title */}
        <h1 style={{ margin: '16px 0', fontSize: 26, fontWeight: 800, color: 'var(--c-text)' }}>
          Apply
        </h1>

        {/* Tier + score card */}
        <div style={{
          borderRadius: 20,
          background: 'linear-gradient(135deg, #2E82D6, #1260B0)',
          padding: '20px 20px 24px',
          marginBottom: 20,
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Decorative circle */}
          <div style={{
            position: 'absolute', top: -20, right: -20,
            width: 120, height: 120,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)',
          }} aria-hidden />

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <Star size={14} fill="white" color="white" strokeWidth={0} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
              {TIER_DISPLAY[tier]}
            </span>
          </div>

          <p style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: '#ffffff' }}>
            {formatKwacha(MIN_LOAN)} – {formatKwacha(maxLoanForTier(tier))}
          </p>
          <p style={{ margin: '0 0 14px', fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>
            Your score qualifies you for these loan amounts
          </p>

          <div style={{ borderTop: '1px solid rgba(255,255,255,0.18)', paddingTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <ScoreRing score={score} />
              <div>
                <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>Score</p>
                <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#ffffff' }}>{score}</p>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, fontSize: 11, color: 'rgba(255,255,255,0.65)' }}>Limit range</p>
              <p style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#ffffff' }}>
                K{MIN_LOAN_KWACHA} – K{maxKwacha.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Loan details card */}
        <div style={{
          background: '#ffffff',
          borderRadius: 20,
          padding: 20,
          boxShadow: '0 2px 16px rgba(15,26,44,0.06)',
          border: '1px solid var(--c-border)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--c-text)' }}>Loan Details</h2>

          {/* Amount */}
          <div>
            <label
              htmlFor="amount-input"
              style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--c-text-sec)', marginBottom: 6 }}
            >
              Amount (Kwacha)
            </label>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              borderRadius: 12,
              border: '1.5px solid var(--c-border)',
              background: 'var(--c-surface-alt)',
              padding: '12px 14px',
            }}>
              <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--c-text-muted)', marginRight: 8 }}>K</span>
              <input
                id="amount-input"
                type="number"
                inputMode="numeric"
                min={MIN_LOAN_KWACHA}
                max={maxKwacha}
                placeholder={`${MIN_LOAN_KWACHA} – ${maxKwacha.toLocaleString()}`}
                value={amountStr}
                onChange={(e) => setAmountStr(e.target.value)}
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  fontSize: 16,
                  color: 'var(--c-text)',
                }}
                aria-label="Amount (Kwacha)"
              />
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--c-text-muted)' }}>
              Min K{MIN_LOAN_KWACHA.toLocaleString()} · Max K{maxKwacha.toLocaleString()}
            </p>
          </div>

          {/* Term */}
          <div>
            <label
              htmlFor="term-select"
              style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--c-text-sec)', marginBottom: 6 }}
            >
              Term (weeks)
            </label>
            <select
              id="term-select"
              value={term}
              onChange={(e) => setTerm(Number(e.target.value) as LoanTermDays)}
              style={{
                width: '100%',
                padding: '13px 14px',
                borderRadius: 12,
                border: '1.5px solid var(--c-border)',
                background: 'var(--c-surface-alt)',
                fontSize: 15,
                fontWeight: 500,
                color: 'var(--c-text)',
                outline: 'none',
                appearance: 'none',
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236B7D93' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 14px center',
                paddingRight: 36,
              }}
              aria-label="Term (weeks)"
            >
              {LOAN_TERM_DAYS.map((d) => (
                <option key={d} value={d}>{TERM_LABELS[d]}</option>
              ))}
            </select>
          </div>

          {/* Purpose */}
          <div>
            <label
              htmlFor="purpose-input"
              style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--c-text-sec)', marginBottom: 6 }}
            >
              Purpose
            </label>
            <input
              id="purpose-input"
              type="text"
              placeholder="e.g. Business stock, school fees"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              style={{
                width: '100%',
                padding: '13px 14px',
                borderRadius: 12,
                border: '1.5px solid var(--c-border)',
                background: 'var(--c-surface-alt)',
                fontSize: 15,
                color: 'var(--c-text)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <Button block size="lg" onClick={handleApply}>
            Apply for a microloan
          </Button>
        </div>
      </main>

      <NavBar />
    </div>
  );
}
